// Global Settings screen: owns edit mode and the data-loading effects, and
// composes the schema fields from the shared dynamic-form module together with
// the metadata, execution conditions and feature panels around them.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { Flex, Grid, useNotifications } from '@decisiv/ui-components';
import { t } from '@lingui/macro';
import noop from 'lodash/noop';
import { useNavigate } from 'react-router-dom';

import {
  groupSchema,
  isFieldFilled,
  normalizeSchema,
  sortSchema,
  useDynamicForm,
} from '../../../features/dynamic-form';
import IntegrationsApi from '../../../api/integrations';
import ConfiguredIntegrationsApi from '../../../api/configured_integrations';
import CommandsApi from '../../../api/commands';
import ActionsApi from '../../../api/actions';
import authorizeOn from '../../../utils/resourceAuthorizer';
import { buildSchemaContent } from '../../../utils/buildSchemaContent';
import { buildPayloadSources } from '../../../utils/payloadSchemaTree';
import OverlayLoading from '../../../components/OverlayLoading';
import WrapperWithLoading from '../../../components/WrapperWithLoading';
import IntegrationSettingsSidebar from '../IntegrationSettingsSidebar';
import { IntegrationSettingsForm, IntegrationWrapper } from '../style';
import SettingsFormBody from './SettingsFormBody';
import SettingsModals from './SettingsModals';
import dynamicFormTexts from './texts';
import useCacheJob from './useCacheJob';
import useExecutionConditions from './useExecutionConditions';
import useGlobalFieldRenderer from './useGlobalFieldRenderer';
import useIntegrationArchive from './useIntegrationArchive';
import useIntegrationPanels from './useIntegrationPanels';
import useIntegrationSubmit from './useIntegrationSubmit';
import useLocationSettingsAttributes from './useLocationSettingsAttributes';
import useMetadataEditor from './useMetadataEditor';

const TRIGGER_TYPE_HTTP_API_REQUEST = 'http_api_request';
const TRIGGER_TYPE_APPLICATION_EVENT = 'application_event';

// Global settings seed the inherited command index from '0' — the location
// settings screen seeds it from '1'.
const GLOBAL_SETTINGS_INDEX_SEED = '0';

const IntegrationSettings = ({ onIntegrationUpdated = noop, ...props }) => {
  const navigate = useNavigate();
  const attrs = props.integration.attributes;

  const [edit, setEdit] = useState(true);
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingState, setLoadingState] = useState(false);
  const [integrationEnabled, setIntegrationEnabled] = useState(attrs.enabled);
  const [actionMetadataSchema, setActionMetadataSchema] = useState(null);
  const [schemasByCommand, setSchemasByCommand] = useState([]);
  const [schemasContent, setSchemasContent] = useState({
    input: '',
    output: '',
  });
  const [schemaDetailsVisible, setSchemaDetailsVisible] = useState(false);
  const [hasConfiguredIntegrations, setHasConfiguredIntegrations] =
    useState(false);
  const formEl = useRef(null);

  const { notify } = useNotifications();
  const texts = useMemo(() => dynamicFormTexts(), []);

  const notifyResult = useCallback(
    (intent, title, body) => notify({ intent, title, onClose: noop }, body),
    [notify],
  );

  const setError = useCallback((name, message) => {
    setErrors((previous) => ({ ...previous, [name]: message }));
  }, []);

  const groupedStaticAttributes = useMemo(() => {
    if ((attrs.global_settings_attributes || []).length === 0) return {};

    return groupSchema(
      sortSchema(
        normalizeSchema(attrs.global_settings_attributes, attrs.commands_flow, {
          initialIndex: GLOBAL_SETTINGS_INDEX_SEED,
        }),
      ),
      'command_name',
    );
  }, [attrs.global_settings_attributes, attrs.commands_flow]);

  const payloadSources = useMemo(
    () => buildPayloadSources(attrs.schemas),
    [attrs.schemas],
  );

  const form = useDynamicForm({
    schema: groupedStaticAttributes,
    initialAttributes: attrs.global_settings_attributes,
  });

  const panels = useIntegrationPanels({
    integration: props.integration,
    setDisableSubmit,
    setError,
  });
  const metadataEditor = useMetadataEditor({
    integration: props.integration,
    setDisableSubmit,
    setError,
  });
  const conditions = useExecutionConditions({
    integration: props.integration,
    setDisableSubmit,
    setError,
  });
  const archive = useIntegrationArchive({
    integration: props.integration,
    notify: notifyResult,
    navigate,
  });
  const cacheJob = useCacheJob({ integrationId: props.integration?.id });
  const locationSettingsHandlers = useLocationSettingsAttributes({
    values: form.values,
    setValue: form.setValue,
  });

  const showCacheSettings = (attrs.trigger_type || []).includes(
    TRIGGER_TYPE_HTTP_API_REQUEST,
  );
  const showAutomaticRetrySettings = (attrs.trigger_type || []).includes(
    TRIGGER_TYPE_APPLICATION_EVENT,
  );

  useEffect(() => {
    const actionUuid = attrs.action_uuid;
    if (!actionUuid) return undefined;

    let cancelled = false;
    (async () => {
      const response = await ActionsApi.fetchActionDetail(actionUuid);
      if (!cancelled && response.data) {
        setActionMetadataSchema(
          response.data.attributes.metadata_schema || null,
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [attrs.action_uuid]);

  // Keyed on the *contents* of commands_flow, not the array itself: every parent
  // refetch hands down a fresh object, and depending on the reference refetched
  // the command schemas even when the flow was identical.
  const commandsFlowKey = (attrs.commands_flow || []).join(',');

  useEffect(() => {
    async function getSchemasByCommand() {
      if (props.integration?.id) {
        setSchemasByCommand(
          await CommandsApi.fetchSchemasGroupedByCommand(attrs.commands_flow),
        );
      }
    }

    getSchemasByCommand();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.integration?.id, commandsFlowKey]);

  useEffect(() => {
    async function checkConfiguredIntegrations() {
      if (props.integration?.id) {
        const { data } =
          await ConfiguredIntegrationsApi.fetchConfiguredIntegrations(
            props.integration.id,
          );
        setHasConfiguredIntegrations(data && data.length > 0);
      }
    }

    checkConfiguredIntegrations();
  }, [props.integration?.id]);

  useEffect(() => {
    form.reset(attrs.global_settings_attributes);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.integration]);

  useEffect(() => {
    form.initJsonToggles(attrs.global_settings_attributes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedStaticAttributes, edit]);

  // Validation runs against the value just typed, not against state, which is
  // still a keystroke behind inside this handler.
  const validate = useCallback(
    (name, value) => {
      form.validateValue(name, value);
      setDisableSubmit(!isFieldFilled({ name, value }));
    },
    [form],
  );

  const validateField = useCallback(
    (name) => validate(name, form.values[name]),
    [validate, form.values],
  );

  const handleFieldChange = useCallback(
    (name, value) => {
      form.setValue(name, value);
      validate(name, value);
    },
    [form, validate],
  );

  const handleHeaderValidate = useCallback(
    (inputName, isBlank) => {
      form.setHeaderError(inputName, isBlank);
      setDisableSubmit(isBlank);
    },
    [form],
  );

  const renderField = useGlobalFieldRenderer({
    form,
    readOnly: edit,
    texts,
    locationSettingsHandlers,
    onChange: handleFieldChange,
    onBlur: validateField,
    onHeaderValidate: handleHeaderValidate,
  });

  const resetAll = useCallback(
    (data) => {
      form.reset(data.attributes.global_settings_attributes);
      metadataEditor.reset(data);
      conditions.reset(data);
      panels.reset(data);
      setErrors({});
    },
    [form, metadataEditor, conditions, panels],
  );

  const toggleEdit = () => {
    setEdit(!edit);
    setDisableSubmit(!edit);
  };

  const submitter = useIntegrationSubmit({
    integration: props.integration,
    form,
    metadataEditor,
    conditions,
    panels,
    setDisableSubmit,
    notifyResult,
    onSaved: useCallback(
      (data) => {
        toggleEdit();
        resetAll(data);
        onIntegrationUpdated(data);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [resetAll, onIntegrationUpdated, edit],
    ),
  });

  const integrationAction = integrationEnabled ? t`DEACTIVATE` : t`ACTIVATE`;

  const toggleIntegrationStatus = async (field) => {
    setLoadingState(true);

    const value = field === 'enabled' ? integrationEnabled : '';
    const response = await IntegrationsApi.toggleIntegration(
      props.integration.id,
      !value,
      field,
    );
    setLoadingState(false);

    if (!response.data) {
      notifyResult(
        'danger',
        `Something went wrong!`,
        response?.errors[0].detail,
      );
      return;
    }

    props.updateIntegrationStatus(!value, field);

    if (field === 'enabled') {
      setIntegrationEnabled(!value);
      notifyResult(
        'success',
        `Integration ${integrationAction}D`,
        `${attrs.name} has been ${integrationAction.toLocaleLowerCase()}d.`,
      );
    }
  };

  const handleSchemaDetailsOpen = (commandName) => {
    setSchemasContent(
      buildSchemaContent(commandName, schemasByCommand, attrs.schemas),
    );
    setSchemaDetailsVisible(true);
  };

  const hasGlobalSettings = (attrs.global_settings_attributes || []).length > 0;

  return (
    <>
      <SettingsModals
        integrationName={attrs.name}
        schema={{
          content: schemasContent,
          visible: schemaDetailsVisible,
          setVisible: setSchemaDetailsVisible,
        }}
        archive={archive}
        cacheJob={cacheJob}
        removal={submitter}
      />
      <IntegrationWrapper>
        <Flex marginX={1}>
          <Grid.Container>
            <Grid.Row>
              <Grid.Column span="3">
                <IntegrationSettingsSidebar
                  integration={props.integration}
                  audience={attrs.audience}
                  serviceNetwork={attrs.service_network}
                  triggerTypes={attrs.trigger_type}
                  action={attrs.action}
                  applicationEvents={attrs.events}
                  authorizeOn={authorizeOn}
                  loadingState={loadingState}
                  integrationAction={integrationAction}
                  integrationConfigured={() => form.isConfigured}
                  edit={edit}
                  hasGlobalSettings={hasGlobalSettings}
                  hasConfiguredIntegrations={hasConfiguredIntegrations}
                  handleToggleArchiveModal={archive.open}
                  handleToggleIntegrationStatus={toggleIntegrationStatus}
                  handleEditIntegration={() =>
                    navigate(`/integrations/${props.integration?.id}/edit`)
                  }
                />
              </Grid.Column>
              <Grid.Column span="9">
                <IntegrationSettingsForm ref={formEl}>
                  {submitter.saving && (
                    <OverlayLoading
                      loading={submitter.saving}
                      message={t`Saving...`}
                    />
                  )}
                  <Grid.Container>
                    <Grid.Row>
                      <Grid.Column>
                        <WrapperWithLoading loading={loading}>
                          <SettingsFormBody
                            integrationName={attrs.name}
                            edit={edit}
                            errors={errors}
                            canEdit={authorizeOn()}
                            onEdit={() => {
                              toggleEdit();
                              resetAll(props.integration);
                            }}
                            onOpenSchemaDetails={handleSchemaDetailsOpen}
                            groupedStaticAttributes={groupedStaticAttributes}
                            renderField={renderField}
                            metadataAndConditions={{
                              actionMetadataSchema,
                              metadata: metadataEditor.metadata,
                              metadataHandlers: metadataEditor.handlers,
                              executionConditions:
                                conditions.executionConditions,
                              executionConditionsHandlers: conditions.handlers,
                              payloadSources,
                              onConditionTypeChange:
                                conditions.setConditionType,
                              autoEnable: panels.autoEnable,
                              onAutoEnableChange: panels.setAutoEnable,
                            }}
                            panels={panels}
                            showCacheSettings={showCacheSettings}
                            showAutomaticRetrySettings={
                              showAutomaticRetrySettings
                            }
                            onToggleCacheModal={cacheJob.toggle}
                            onShowTtlField={() =>
                              panels.handleShowTtlField(errors)
                            }
                            saveDisabled={disableSubmit || !form.isConfigured}
                            onSave={submitter.submit}
                            onCancel={() => {
                              toggleEdit();
                              resetAll(props.integration);
                            }}
                          />
                        </WrapperWithLoading>
                      </Grid.Column>
                    </Grid.Row>
                  </Grid.Container>
                </IntegrationSettingsForm>
              </Grid.Column>
            </Grid.Row>
          </Grid.Container>
        </Flex>
      </IntegrationWrapper>
    </>
  );
};

export default IntegrationSettings;

IntegrationSettings.propTypes = {
  integration: PropTypes.oneOfType([PropTypes.object]).isRequired,
  updateIntegrationStatus: PropTypes.func.isRequired,
  onIntegrationUpdated: PropTypes.func,
};
